// Expense-create retry identity and payload helpers. One localStorage attempt
// survives remounts: unchanged retries reuse its key and exact submitted body;
// edited uncertain retries are blocked until the original details are restored.

export type ExpenseCreatePaymentMode = "pay_now" | "on_account";

export type ExpenseCreateLine = {
  detail: string | null;
  amount: string;
};

export type ExpenseCreatePayload = {
  payment_mode: ExpenseCreatePaymentMode;
  date: string;
  account_id: number;
  lines: ExpenseCreateLine[];
  description: string | null;
  vendor_id: number | null;
  payment_account_id: number | null;
  due_date: string | null;
  source_document_id: number | null;
};

/** Actual POST /expenses body, excluding the separately stored identity. */
export type ExpenseCreateRequest = {
  date: string;
  account_id: number;
  amount: number;
  description: string;
  lines: { detail: string; amount: number }[];
  source_document_id?: number;
} & (
  | { status: "booked"; payment_account_id: number; vendor_id?: never; due_date?: never }
  | { status: "unpaid"; vendor_id: number; due_date: string; payment_account_id?: never }
);

/**
 * Legacy canonical representation retained for compatibility. ExpenseEntry
 * now fingerprints its actual request body so restoration includes every
 * submitted field. Neither client representation is the server-side hash. The backend computes its own
 * authoritative SHA-256 server-side (expenses_router._expense_create_fingerprint)
 * from the request it parsed; this frontend copy only needs to be internally
 * consistent so that "the same form re-submitted" and "the form edited"
 * are told apart reliably.
 *
 * - payment_mode: the RESOLVED posting path the backend will take
 *   (payment_account_id present -> "pay_now", else "on_account"), computed
 *   by the caller the same way the backend branches -- so switching payment
 *   source counts as a material edit.
 * - account_id / vendor_id / payment_account_id / source_document_id:
 *   canonicalized to numbers (or null) so "12" and 12 never differ.
 * - lines: order preserved; detail passed through (empty string -> null);
 *   amount fixed to a 2-decimal string. This app has no frontend-visible
 *   company money-scale setting anywhere (mirrors canonicalDepositCreate's
 *   identical rationale) -- 2 decimals matches the backend default
 *   CompanySettings.money_scale of 2.
 * - description: passed through, empty string canonicalized to null.
 * - date / due_date: the exact submitted YYYY-MM-DD strings (due_date null
 *   for pay_now).
 */
export function canonicalExpenseCreate(args: {
  paymentMode: ExpenseCreatePaymentMode;
  date: string;
  accountId: string | number;
  lines: { detail?: string | null; amount: string | number }[];
  description?: string | null;
  vendorId?: string | number | null;
  paymentAccountId?: string | number | null;
  dueDate?: string | null;
  sourceDocumentId?: string | number | null;
}): ExpenseCreatePayload {
  const toNumOrNull = (v: string | number | null | undefined): number | null =>
    v === null || v === undefined || v === "" ? null : Number(v);

  return {
    payment_mode: args.paymentMode,
    date: args.date,
    account_id: Number(args.accountId),
    lines: args.lines.map((l) => ({
      detail: l.detail == null || l.detail === "" ? null : l.detail,
      amount: Number(l.amount).toFixed(2),
    })),
    description:
      args.description == null || args.description === "" ? null : args.description,
    vendor_id: toNumOrNull(args.vendorId),
    payment_account_id: toNumOrNull(args.paymentAccountId),
    due_date: args.dueDate ?? null,
    source_document_id: toNumOrNull(args.sourceDocumentId),
  };
}

export function fingerprintExpenseCreate(payload: ExpenseCreatePayload | ExpenseCreateRequest): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt. The next submission always mints a fresh key.
 * - "in-flight": a request has been sent and its outcome is not yet known.
 * - "ambiguous": the last attempt's outcome could not be determined (network
 *   failure, timeout, unreadable response, HTTP 500, or any other unexpected
 *   server error). The key, fingerprint and original body are retained.
 *   An unchanged retry reuses them; an edited retry is blocked.
 */
export type ExpenseCreateAttemptState = "idle" | "in-flight" | "ambiguous";

export type ExpenseCreateAttempt = {
  state: ExpenseCreateAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: ExpenseCreateRequest | null;
};

export const IDLE_EXPENSE_CREATE_ATTEMPT: ExpenseCreateAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type ExpenseCreateSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: ExpenseCreateAttempt }
  | { kind: "blocked-in-flight" }
  | { kind: "blocked-mismatch" };

/**
 * Pure decision for whether a submit attempt may proceed, given the CURRENT
 * attempt state and the fingerprint just built from the form. Never mutates
 * `attempt` -- returns a NEW attempt (state "in-flight") to store when
 * kind === "start".
 *
 *  - in-flight -> blocked-in-flight (defensive; the component's synchronous
 *    in-flight ref already prevents reaching here in practice).
 *  - ambiguous, SAME fingerprint  -> unchanged retry: reuse the stored key
 *    exactly (safe replay -- never mints a new one).
 *  - ambiguous, DIFFERENT fingerprint -> block; caller restores the original
 *    submitted details without rotating the key.
 *  - idle -> mint a fresh key.
 */
export function decideExpenseCreateAttempt(
  attempt: ExpenseCreateAttempt,
  payload: ExpenseCreateRequest,
  generateKey: () => string,
): ExpenseCreateSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  const fingerprint = fingerprintExpenseCreate(payload);
  if (attempt.state === "ambiguous") {
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay). Back to idle. */
export function resolveExpenseCreateSuccess(): ExpenseCreateAttempt {
  return IDLE_EXPENSE_CREATE_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's stack
 * can produce -- 400 from this handler (amount <= 0, deposit path,
 * missing/invalid vendor/account), 401/403 from auth dependencies, 422 from
 * FastAPI request validation, a closed-period 409, or an idempotency-conflict
 * 409. Preserve the existing reset policy. An identity conflict can refer to
 * an earlier completed request; resetting local state does not release that
 * server identity or establish the outcome of an earlier uncertain request.
 */
export function resolveExpenseCreateDefiniteRejection(): ExpenseCreateAttempt {
  return IDLE_EXPENSE_CREATE_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/unreadable response,
 * HTTP 500, or any other unexpected server error where the operation may have
 * committed before the response was constructed. Retains the key, fingerprint
 * and body so an unchanged retry replays; a materially edited retry is blocked.
 */
export function resolveExpenseCreateAmbiguousFailure(
  attempt: ExpenseCreateAttempt,
): ExpenseCreateAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Rehydrate a persisted attempt (e.g. from localStorage) across a component
 * remount. A persisted "in-flight" means a request was sent but its outcome
 * was never observed (tab closed / navigated away mid-request) -- exactly the
 * ambiguous case -- so it is coerced to "ambiguous" (retaining key +
 * fingerprint) rather than trusted as still in flight. Anything unrecognized
 * or missing a key falls back to idle.
 */
function isExpenseCreateRequest(raw: unknown): raw is ExpenseCreateRequest {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const p = raw as Record<string, unknown>;
  const positiveNumber = (v: unknown): v is number =>
    typeof v === "number" && Number.isFinite(v) && v > 0;
  const id = (v: unknown) => positiveNumber(v) && Number.isSafeInteger(v);
  const date = (v: unknown): v is string => {
    if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const parsed = new Date(v + "T00:00:00Z");
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v;
  };
  const allowed = ["date", "account_id", "amount", "description", "lines",
    "source_document_id", "status", "payment_account_id", "vendor_id", "due_date"];
  if (Object.keys(p).some(key => !allowed.includes(key)) ||
      !date(p.date) || !id(p.account_id) || !positiveNumber(p.amount) ||
      typeof p.description !== "string" ||
      (p.source_document_id !== undefined && !id(p.source_document_id)) ||
      !Array.isArray(p.lines) || p.lines.length === 0 ||
      !p.lines.every(line => line && typeof line === "object" && !Array.isArray(line) &&
        Object.keys(line).every(key => key === "detail" || key === "amount") &&
        typeof line.detail === "string" && positiveNumber(line.amount))) return false;
  if (p.status === "booked") {
    return id(p.payment_account_id) && p.vendor_id === undefined && p.due_date === undefined;
  }
  return p.status === "unpaid" && id(p.vendor_id) && date(p.due_date) &&
    p.payment_account_id === undefined;
}

export function hydrateExpenseCreateAttempt(raw: unknown): ExpenseCreateAttempt {
  if (!raw || typeof raw !== "object") return IDLE_EXPENSE_CREATE_ATTEMPT;
  const a = raw as Partial<ExpenseCreateAttempt>;
  if ((a.state !== "ambiguous" && a.state !== "in-flight") ||
      typeof a.idempotencyKey !== "string" || !a.idempotencyKey.trim() ||
      typeof a.submittedFingerprint !== "string" ||
      !isExpenseCreateRequest(a.submittedPayload) ||
      fingerprintExpenseCreate(a.submittedPayload) !== a.submittedFingerprint) {
    return IDLE_EXPENSE_CREATE_ATTEMPT;
  }
  return {
    state: "ambiguous",
    idempotencyKey: a.idempotencyKey,
    submittedFingerprint: a.submittedFingerprint,
    submittedPayload: a.submittedPayload,
  };
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type ExpenseCreateFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Mirrors classifyDepositCreateFailure exactly:
 *
 * - no numeric status (network failure/timeout/unreadable) -> ambiguous.
 * - 409 -> closed-period if isClosedPeriod matches its message, else
 *   idempotency-conflict (both share HTTP 409).
 * - other 4xx (400/401/403/422/...) -> definite-rejection (nothing persisted;
 *   the failure occurred before or in place of any business write).
 * - 5xx and anything else -> ambiguous (get_expense reload runs AFTER commit,
 *   so a 500 can mean the Expense WAS persisted and only response
 *   construction failed). Fails closed.
 */
export function classifyExpenseCreateFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): ExpenseCreateFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    return "ambiguous";
  }
  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }
  if (status >= 400 && status < 500) {
    return "definite-rejection";
  }
  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown for an idempotency-conflict 409: the current key is permanently bound
 * to a different, already-completed request. Deliberately does NOT suggest the
 * expense may ambiguously have been recorded -- THIS attempt's data was never
 * persisted under this key.
 */
export const EXPENSE_CREATE_CONFLICT_MESSAGE =
  "Couldn't record this expense. Check the Expenses list before trying again.";

/**
 * Shown on an ambiguous failure: the prior attempt's outcome is unknown, so
 * it may or may not have been recorded. Conveys that it may already have
 * posted and the Expenses list must be checked before retrying. Retry
 * identity and unchanged-body restoration remain governed by the state machine.
 */
export const EXPENSE_CREATE_AMBIGUOUS_MESSAGE =
  "This expense may already be saved. Check the Expenses list before retrying.";
